import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ChevronLeft, ExternalLink, Download, Loader2, RotateCcw, Upload, Copy, Mail, Check, Globe, ShieldCheck, AlertCircle, FileArchive, Plus, Minus, SlidersHorizontal } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Splotch, BrandCharacter } from '@/components/brand';
import {
  SITE_TYPES, saveSiteType, setSiteAddon, type SiteType, type SiteAddons,
  fetchMySite, checkHandle, createSite, uploadSiteZip, publishSite, rollbackSite, siteDownloadUrl,
  fetchSiteMessages, markSiteMessagesRead, saveNotifyEmail, startHosting, stopHosting, fetchDomains, addDomain, removeDomain,
  setPrimaryHost, renameSite, deleteSite,
  type MySite, type Site, type SiteMessages, type SiteDomain, type HostingOffer, type HostingChoice,
  startSiteImport, getSiteImport, type SiteImport, linkSiteToProfile,
} from '@/services/siteApi';
import SitePostsPanel from '@/components/SitePostsPanel';
import SiteMediaLinks from '@/components/SiteMediaLinks';
import SiteAgencyPanel from '@/components/SiteAgencyPanel';
import SiteScan, { type Fit } from '@/components/SiteScan';
import SiteVideosPanel from '@/components/SiteVideosPanel';
import SiteDesigner from '@/components/SiteDesigner';
import SiteStorePanel from '@/components/SiteStorePanel';
import SitePromptBuilder from '@/components/SitePromptBuilder';
import SiteRestyle from '@/components/SiteRestyle';
import SitePhotoTodo from '@/components/SitePhotoTodo';
import SiteTypeQuestion from '@/components/SiteTypeQuestion';
import { SiteSharePanel, SiteSearchPanel } from '@/components/SiteSharePanel';
import { SiteGithubPanel } from '@/components/SiteGithubPanel';

// Site hosting. Two moods: before hosting, a short pitch with the address box and one
// button; with a site, home base (the site big at the top, messages, domain, versions,
// plan). Updating the site is its own focused view: upload, check, preview, publish.
// Design: the "My Site page redesign" canvas (Oct 18). Spec: docs/SCOPE_site_hosting.md.

const INK = 'var(--hj-ink)';
const MUTED = 'var(--hj-ink-2)';
const panel: React.CSSProperties = { background: '#fff', border: '1px solid var(--hj-hairline-soft)', borderRadius: 16, padding: 24 };
const h2: React.CSSProperties = { margin: 0, fontFamily: 'var(--hj-font-display)', fontWeight: 800, fontSize: 20, letterSpacing: '-0.01em', color: INK };
const body: React.CSSProperties = { fontSize: 15, color: MUTED, margin: 0, lineHeight: 1.5 };
const small: React.CSSProperties = { fontSize: 14, color: MUTED, margin: 0, lineHeight: 1.45 };
const errText: React.CSSProperties = { ...small, color: 'var(--hj-pink-ink)' };
const btnBase: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 48, padding: '12px 20px',
  borderRadius: 12, fontFamily: 'inherit', fontWeight: 600, fontSize: 15, textDecoration: 'none', cursor: 'pointer', boxSizing: 'border-box',
};
const primary: React.CSSProperties = { ...btnBase, background: INK, color: '#fff', border: 0 };
const secondary: React.CSSProperties = { ...btnBase, background: '#fff', color: INK, border: '1.5px solid var(--hj-ink)' };
const quiet: React.CSSProperties = { ...btnBase, minHeight: 40, padding: '8px 14px', fontSize: 14, fontWeight: 500, background: '#fff', color: INK, border: '1px solid var(--hj-hairline)', borderRadius: 10 };
const linkBtn: React.CSSProperties = { background: 'none', border: 0, padding: 0, fontFamily: 'inherit', fontSize: 14, fontWeight: 600, color: INK, textDecoration: 'underline', cursor: 'pointer', minHeight: 32 };
const disabled = (s: React.CSSProperties, off: boolean): React.CSSProperties => (off ? { ...s, opacity: 0.45, cursor: 'not-allowed' } : s);
const pill = (bg: string, fg: string): React.CSSProperties => ({ display: 'inline-flex', alignItems: 'center', gap: 6, background: bg, color: fg, fontWeight: 600, fontSize: 13, padding: '4px 10px', borderRadius: 999 });
const LIVE_BG = '#e3f1d3', LIVE_FG = '#2f5d1a';

const day = (d: string | null) => (d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '');
const when = (d: string | null) => (d ? new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
const trim0 = (x: string) => x.replace(/\.0$/, '');
const served = (b: number) => (b >= 1e9 ? `${trim0((b / 1e9).toFixed(1))} GB` : `${Math.max(1, Math.round(b / 1e6))} MB`);
const size = (b: number) => (b >= 1024 * 1024 * 1024 ? `${trim0((b / 1024 / 1024 / 1024).toFixed(2).replace(/0$/, ''))} GB` : b > 1024 * 1024 ? `${trim0((b / 1024 / 1024).toFixed(1))} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const price = (o: { interval?: string; priceCents?: number }) => { const c = o.priceCents || 500; return `$${c % 100 ? (c / 100).toFixed(2) : (c / 100).toFixed(0)}/${o.interval === 'year' ? 'year' : 'month'}`; };
const PENDING_HANDLE = 'hj_site_pending_handle';
const remember = (h: string) => { try { localStorage.setItem(PENDING_HANDLE, h); } catch { /* private window */ } };
const recall = () => { try { return localStorage.getItem(PENDING_HANDLE) || ''; } catch { return ''; } };

const MySitePage: React.FC = () => {
  const [data, setData] = useState<MySite | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'home' | 'update'>('home');
  // ?move=<address> (from /scan or the home page): open Move my site with it filled in.
  const [params] = useSearchParams();
  const moveUrl = (params.get('move') || '').trim() || null;
  useEffect(() => { if (moveUrl && data?.site) setView('update'); }, [moveUrl, data?.site?.id]);
  const reload = () => fetchMySite().then(setData).catch((e) => setError(e.message));
  useEffect(() => { reload(); }, []);
  useEffect(() => { window.scrollTo(0, 0); }, [view]);
  const setSite = (site: Site) => setData((d) => (d ? { ...d, site } : d));
  // "What kind of site are you building?" comes before hosting. A talent account is
  // building an acting site, so it goes straight in; everyone else answers first,
  // with their account's likely answer offered (Joe, Nov 2 2026).
  const [askType, setAskType] = useState(false);
  const suggested = data?.suggestedTypes || [];
  const needType = !!data && !data.site && !data.siteType && !(suggested.length === 1 && suggested[0] === 'acting');
  const chosen = (t: SiteType) => { setAskType(false); setData((d) => (d ? { ...d, siteType: t } : d)); };

  if (!data && !error) {
    return (
      <main className="min-h-screen flex items-center justify-center" style={{ background: 'var(--hj-bg)' }}>
        <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--hj-ink-3)' }} />
      </main>
    );
  }
  const site = data?.site || null;
  return (
    <main style={{ background: 'var(--hj-bg)', color: INK, minHeight: 'calc(100vh - 68px)' }}>
      <div className="mx-auto" style={{ maxWidth: 1120, padding: '0 16px 96px' }}>
        <div className="flex items-center gap-2.5" style={{ padding: '20px 0 14px', fontSize: 13 }}>
          {view === 'update' ? (
            <button type="button" onClick={() => setView('home')} className="inline-flex items-center gap-1.5" style={{ ...linkBtn, textDecoration: 'none', fontSize: 15, minHeight: 44 }}>
              <ChevronLeft size={16} strokeWidth={2} /> Back to my site
            </button>
          ) : (
            <>
              <Link to="/account" className="inline-flex items-center gap-1.5" style={{ color: 'var(--hj-ink-3)', textDecoration: 'none' }}><ChevronLeft size={12} strokeWidth={2} /> Account</Link>
              <span style={{ color: 'var(--hj-ink-4)' }}>/</span><span>My Site</span>
            </>
          )}
        </div>

        {error && <div style={{ ...panel, borderColor: 'var(--hj-pink)', color: 'var(--hj-pink-ink)', marginBottom: 20 }}>{error}</div>}

        {data && !site && (needType || askType) && (
          <SiteTypeQuestion suggested={askType ? [] : suggested} current={data.siteType || null} showAll={askType} onChosen={chosen} onCancel={askType ? () => setAskType(false) : undefined} />
        )}
        {data && !site && !needType && !askType && <FirstVisit data={data} onCreated={setSite} moveUrl={moveUrl} onChangeType={() => setAskType(true)} />}
        {data && site && view === 'home' && <HomeBase data={data} site={site} onSite={setSite} onUpdate={() => setView('update')} onError={setError} onReload={reload} />}
        {data && site && view === 'update' && <UpdateView site={site} onSite={setSite} onDone={() => setView('home')} moveUrl={moveUrl} />}
      </div>
    </main>
  );
};

/* ---------- First visit: pitch, address, one button ---------- */

const FirstVisit: React.FC<{ data: MySite; onCreated: (s: Site) => void; moveUrl?: string | null; onChangeType: () => void }> = ({ data, onCreated, moveUrl, onChangeType }) => {
  const offer = data.offer;
  const type: SiteType = data.siteType || (data.suggestedTypes || [])[0] || 'acting';
  const acting = type === 'acting';
  const noun = (SITE_TYPES.find((t) => t.id === type) || SITE_TYPES[0]).noun;
  return (
    <div className="flex flex-col" style={{ gap: 40 }}>
      <TutorialBanner />
      <section className="flex flex-col" style={{ ...panel, padding: 28, gap: 12 }}>
        <SiteScan context="landing" />
      </section>
      <section className="flex flex-wrap items-center" style={{ background: 'var(--hj-beige)', color: 'var(--hj-beige-ink)', borderRadius: 20, padding: 'clamp(24px, 4vw, 48px)', gap: 32 }}>
        <div className="flex flex-col" style={{ flex: '1 1 440px', minWidth: 0, gap: 18 }}>
          <h1 className="font-hj-display m-0" style={{ fontWeight: 800, fontSize: 'clamp(34px, 5vw, 52px)', lineHeight: 1.02, letterSpacing: '-0.03em' }}>
            {data.enabled ? 'Pick your address.' : acting ? 'Your acting site, hosted with your membership.' : `Your ${noun} site, hosted by Hey Joe.`}
          </h1>
          <p className="m-0" style={{ fontSize: 18, lineHeight: 1.5, color: 'var(--hj-beige-ink-2)', maxWidth: 520 }}>
            {data.enabled
              ? 'This is where your site lives. You can connect your own domain once you’re set up.'
              : 'Bring the site you already have. We host it, keep it fast and safe, and send every contact form message straight to your inbox.'}
          </p>
          {moveUrl && <p className="m-0" style={{ fontSize: 15, lineHeight: 1.5, color: 'var(--hj-beige-ink)', fontWeight: 600 }}>Pick your address and we’ll move {moveUrl} onto it next.</p>}
          <p className="m-0" style={{ fontSize: 15, lineHeight: 1.5, color: 'var(--hj-beige-ink)' }}>
            {acting ? 'Building a site for a company instead? ' : `You’re building a ${noun} site. `}
            <button type="button" onClick={onChangeType} style={{ ...linkBtn, fontSize: 15, color: 'var(--hj-beige-ink)' }}>{acting ? 'Choose a different kind of site' : 'Change'}</button>
          </p>
          <AddressCard data={data} onCreated={onCreated} />
        </div>
        <div aria-hidden="true" className="relative flex items-center justify-center" style={{ flex: '1 1 300px', minWidth: 0, minHeight: 320 }}>
          <Splotch variant={2} size={420} opacity={0.9} rotate={-8} style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%) rotate(-8deg)', maxWidth: '96%' }} />
          <BrandCharacter which="laptop-duo" size={280} tone="original" style={{ position: 'relative', maxWidth: '90%', height: 'auto' }} />
          <div style={{ position: 'absolute', top: 0, right: 8, transform: 'rotate(-5deg)', fontFamily: 'var(--hj-font-script)', fontWeight: 700, fontSize: 28, lineHeight: 0.95, textAlign: 'right' }}>your site,<br />your name.</div>
        </div>
      </section>

      <section className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
        <Benefit icon={<Globe size={26} strokeWidth={1.8} />} title="Your address or your domain">Start on yourname.{data.domain}. Point your own domain at it whenever you’re ready, and we handle the secure certificate.</Benefit>
        <Benefit icon={<Mail size={26} strokeWidth={1.8} />} title="Messages to your inbox">Your contact form keeps working. Every message lands in your email and here, with spam filtered out.</Benefit>
        <Benefit icon={<ShieldCheck size={26} strokeWidth={1.8} />} title="Fast and checked">Served worldwide in a blink. Every upload is scanned before it goes live, and you can go back a version in one click.</Benefit>
      </section>

      {/* Three doors, by situation. Joe, Oct 25 2026: say plainly who does what
          where. Moving a site happens entirely here; starting from scratch can
          happen here (a first draft from the Hey Joe profile) or in an outside
          agent with the prompt; someone else building it gets the prompt too. */}
      <section className="flex flex-col" style={{ gap: 20 }}>
        <h2 className="font-hj-display m-0" style={{ fontWeight: 800, fontSize: 28, letterSpacing: '-0.02em' }}>Which one are you?</h2>
        <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
          <Door title="I have a site">
            Everything happens here. Pick your address, then paste your site’s address and we move it, or upload a zip of its files. You look it over on a private preview, publish, and change it afterwards in plain words.
            <br /><br />
            <span style={{ ...small, fontSize: 13.5 }}>Some hosts block us from reading a site. If that happens, export your site from your host and upload the zip; the check above tells you first.</span>
          </Door>
          <Door title="I don’t have a site yet">
            {acting
              ? <>Two ways. Pick your address and press <strong>Build it for me</strong>: we write a first version from your Hey Joe headshot, reel, résumé and store, and you change it here. Or copy the prompt below into Claude or ChatGPT for more back-and-forth on the design, then upload the zip it gives you.</>
              : <>Two ways. Pick your address, tell us about your {noun} in a few lines and press <strong>Build it for me</strong>: we write a first version and you change it here. Or copy the prompt below into Claude or ChatGPT, then upload the zip it gives you.</>}
          </Door>
          <Door title="Someone is building it for me">
            {acting
              ? 'Send them the prompt below. It already has your headshot, reel, résumé, store and contact form, with permanent links. When they hand you a zip, upload it here and publish.'
              : 'Send them the prompt below. It already has your contact form and any videos on your site. When they hand you a zip, upload it here and publish.'}
          </Door>
        </div>
      </section>

      <section className="flex flex-col" style={{ gap: 20 }}>
        <h2 className="font-hj-display m-0" style={{ fontWeight: 800, fontSize: 28, letterSpacing: '-0.02em' }}>How it works</h2>
        <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
          <Step n={1} title="Pick your address">{acting ? 'Your name, the way casting will type it.' : 'Your company’s name, the way people will type it.'}</Step>
          <Step n={2} title="Bring the site in">Move it from its address, upload a zip, or have us build a first version. We check it and show you a private preview.</Step>
          <Step n={3} title="Publish">One button and it’s live. Change it in plain words whenever you book something new.</Step>
        </div>
      </section>

      <SitePromptBuilder siteType={type} />
      <GetYourFiles />

      {!data.enabled && offer.kind !== 'charter_free' && offer.kind !== 'yearly_free' && (
        <section className="flex flex-wrap items-center justify-between" style={{ background: 'var(--hj-ink)', color: '#fff', borderRadius: 20, padding: '28px clamp(20px, 4vw, 40px)', gap: 20 }}>
          <div className="flex flex-col" style={{ flex: '1 1 420px', minWidth: 0, gap: 8 }}>
            <div className="font-hj-display" style={{ fontWeight: 800, fontSize: 24, letterSpacing: '-0.02em' }}>Moving from another site builder?</div>
            <div style={{ fontSize: 16, lineHeight: 1.5, color: 'oklch(0.88 0.01 80)' }}>Move your site over in one afternoon class, and your first 6 months of hosting are on us. Check whether your site is a fit in 30 seconds.</div>
          </div>
          <Link to="/move-your-site" style={{ ...btnBase, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.006 70)', border: 0 }}>Check my site</Link>
        </section>
      )}

      <section className="flex flex-col" style={{ maxWidth: 720 }}>
        <h2 className="font-hj-display m-0" style={{ fontWeight: 800, fontSize: 28, letterSpacing: '-0.02em', marginBottom: 8 }}>Questions</h2>
        <Faq q="What kind of site can I bring?">Any site made of regular web files: HTML, CSS, images and JavaScript, up to 250 MB (add more storage for $3 a month per GB), plus your video files. Hosting includes 10 GB of bandwidth a month, and you can add 25 GB a month for $5. Videos play through a streaming player, with 1 GB included in hosting and $3 a month for each GB over. Hand-made sites and WordPress exports work. Some site builders can’t export a site, so moving off one means rebuilding it, or pasting its address into Move my site. Server code like PHP or WordPress doesn’t run here.</Faq>
        <Faq q="What happens if I cancel?">Your site shows a “temporarily unavailable” page. Your files and address are kept for 90 days, and you can download your site any time.</Faq>
        <Faq q="Can I keep my own domain?">Yes. Keep it where it is and add one record at your domain company. We show you exactly what to enter and tell you when it’s connected.</Faq>
      </section>
    </div>
  );
};

// "Where do I get my site's files?" by situation. Same facts as the connector's
// get_site_help guide (casting src/backend/server/ai/knowledge/sites.md). The
// prompt itself lives in SitePromptBuilder, the card above this one.
const GetYourFiles: React.FC = () => {
  const classLink = <Link to="/move-your-site" style={{ ...linkBtn, fontSize: 15 }}>Check if my site is a fit for the class</Link>;
  return (
    <section className="flex flex-col" style={{ maxWidth: 720 }}>
      <h2 className="font-hj-display m-0" style={{ fontWeight: 800, fontSize: 28, letterSpacing: '-0.02em', marginBottom: 8 }}>Where do I get my site’s files?</h2>
      <p style={{ ...body, marginBottom: 12 }}>A website is a folder of files. The home page is a file called index.html, and your photos sit next to it. Zip that folder and upload it here.</p>
      <Faq q="My site builder won’t let me download it">
        <span>Some site builders don’t let you download your site. In our Move Your Site class we rebuild it with you as files you own, in one afternoon.</span>
        {classLink}
      </Faq>
      <Faq q="My site is on WordPress">
        <span>Install the free Simply Static plugin, run an export, and upload the zip it makes. Look over the preview before you publish, since some plugins don’t carry over.</span>
      </Faq>
      <Faq q="Someone built it for me">
        <span>Ask them for your site’s files as a zip, with index.html at the top.</span>
      </Faq>
      <Faq q="I don’t have a site yet">
        <span>An AI chat tool like Claude or ChatGPT can write one for you. Copy the prompt above: it already has your headshot, reel and the rest from Hey Joe. Upload the zip it gives you. Or build it with us in the class.</span>
        {classLink}
      </Faq>
      <Faq q="How do I make a zip?">
        <span>On a Mac, right-click the folder and choose Compress. On Windows, right-click it and choose Compress to ZIP file (on older Windows, Send to, then Compressed folder).</span>
      </Faq>
      <div style={{ borderTop: '1px solid var(--hj-hairline)', marginBottom: 20 }} />
      <div className="flex flex-col" style={{ gap: 20 }}>
        {/* The store card sits here on the first visit and the upload view too, not
            only on the home base: someone without a Creator account opens one from
            it instead of the card being missing (Joe, Oct 25 2026). */}
        <SiteStorePanel />
        <SiteAgencyPanel />
        <SiteMediaLinks />
      </div>
    </section>
  );
};

const Benefit: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
  <div className="flex flex-col" style={{ ...panel, padding: 28, gap: 10 }}>
    <span aria-hidden="true">{icon}</span>
    <div className="font-hj-display" style={{ fontWeight: 700, fontSize: 19 }}>{title}</div>
    <p style={body}>{children}</p>
  </div>
);
const Step: React.FC<{ n: number; title: string; children: React.ReactNode }> = ({ n, title, children }) => (
  <div className="flex items-start" style={{ gap: 16 }}>
    <span className="font-hj-display flex items-center justify-center" style={{ flex: 'none', width: 40, height: 40, borderRadius: 20, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.006 70)', fontWeight: 800 }}>{n}</span>
    <div className="flex flex-col" style={{ gap: 4 }}><div style={{ fontWeight: 600, fontSize: 16 }}>{title}</div><p style={body}>{children}</p></div>
  </div>
);
// A door on the first visit: one situation, what happens, where.
const Door: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="flex flex-col" style={{ ...panel, gap: 10, padding: 22 }}>
    <div className="font-hj-display" style={{ fontWeight: 800, fontSize: 20, letterSpacing: '-0.01em', color: INK }}>{title}</div>
    <p style={body}>{children}</p>
  </div>
);
const Faq: React.FC<{ q: string; children: React.ReactNode }> = ({ q, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderTop: '1px solid var(--hj-hairline)' }}>
      <button type="button" aria-expanded={open} onClick={() => setOpen(!open)} className="flex w-full items-center justify-between text-left" style={{ background: 'none', border: 0, padding: '16px 0', minHeight: 52, gap: 12, fontFamily: 'inherit', fontWeight: 600, fontSize: 16, color: INK, cursor: 'pointer' }}>
        {q}{open ? <Minus size={18} aria-hidden="true" /> : <Plus size={18} aria-hidden="true" />}
      </button>
      {open && <div className="flex flex-col" style={{ ...body, paddingBottom: 16, gap: 10 }}>{children}</div>}
    </div>
  );
};

// The address box doubles as the sign-up: before hosting it checks the name and
// remembers it across checkout; once hosting is on it claims the address.
const AddressCard: React.FC<{ data: MySite; onCreated: (s: Site) => void }> = ({ data, onCreated }) => {
  const [handle, setHandle] = useState(recall);
  const [check, setCheck] = useState<{ ok: boolean; problem?: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (!handle) { setCheck(null); return; }
    const t = setTimeout(() => { checkHandle(handle).then(setCheck).catch(() => setCheck(null)); }, 300);
    return () => clearTimeout(t);
  }, [handle]);
  const offer = data.offer;
  const claim = async () => {
    setBusy(true); setErr(null);
    try { const r = await createSite(handle); remember(''); onCreated(r.site); } catch (e) { setErr((e as Error).message); }
    setBusy(false);
  };
  const choices = offer.choices || [];
  const [pick, setPick] = useState<string | undefined>(choices[0]?.id);
  const chosen = choices.find((c) => c.id === pick) || choices[0];
  const canTrial = !data.enabled && !!chosen && chosen.trialDays > 0;
  const start = async (trial = false) => {
    if (offer.kind === 'join_plus') { window.location.href = '/plans'; return; }
    setBusy(true); setErr(null);
    if (handle && check?.ok) remember(handle);
    try {
      const r = await startHosting(chosen?.id, trial);
      if (r.checkoutUrl) { window.location.href = r.checkoutUrl; return; }
      window.location.reload();
    } catch (e) { setErr((e as Error).message); setBusy(false); }
  };
  const off = busy || (data.enabled && !check?.ok);
  return (
    <div className="flex flex-col" style={{ marginTop: 8, maxWidth: 480, background: '#fff', color: INK, borderRadius: 16, padding: 24, gap: 14, boxShadow: '0 1px 0 rgba(40,36,30,0.06), 0 12px 32px rgba(40,36,30,0.08)' }}>
      {offer.kind === 'grant_free' && !data.enabled && <span style={{ ...pill('var(--hj-yellow-tint)', INK), alignSelf: 'flex-start' }}>{freeLabel(offer.freeDays)} of hosting is yours, no card needed</span>}
      {offer.kind === 'charter_free' && !data.enabled && <span style={{ ...pill('var(--hj-yellow-tint)', INK), alignSelf: 'flex-start' }}>Charter members: your first year is free</span>}
      {offer.kind === 'yearly_free' && !data.enabled && <span style={{ ...pill('var(--hj-yellow-tint)', INK), alignSelf: 'flex-start' }}>Yearly members: hosting is free until you renew</span>}
      <label htmlFor="site-handle" style={{ fontWeight: 600, fontSize: 15 }}>Pick your address</label>
      <div className="flex items-center" style={{ border: '1.5px solid var(--hj-ink)', borderRadius: 12, overflow: 'hidden' }}>
        <input id="site-handle" value={handle} placeholder="janedoe" autoComplete="off" autoCapitalize="none" spellCheck={false}
          onChange={(e) => setHandle(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
          style={{ flex: 1, minWidth: 0, border: 0, padding: '14px 2px 14px 16px', font: '500 17px var(--hj-font-ui)', outline: 'none', background: 'transparent', color: INK }} />
        <span style={{ padding: '14px 16px 14px 0', fontSize: 16, color: 'var(--hj-ink-3)', whiteSpace: 'nowrap' }}>.{data.domain}</span>
      </div>
      {check?.ok && <div className="flex items-center" style={{ gap: 8, fontSize: 14, color: LIVE_FG, wordBreak: 'break-all' }}><Check size={16} strokeWidth={2.5} aria-hidden="true" style={{ flex: 'none' }} />{handle}.{data.domain} is yours if you want it</div>}
      {check && !check.ok && <p style={errText}>{check.problem}</p>}
      {!data.enabled && choices.length > 1 && (
        <div role="radiogroup" aria-label="Pick a plan" className="flex flex-col" style={{ gap: 8 }}>
          {choices.map((c) => {
            const on = c.id === chosen?.id;
            return (
              <button key={c.id} type="button" role="radio" aria-checked={on} onClick={() => setPick(c.id)}
                className="flex items-center text-left" style={{ gap: 12, padding: '12px 14px', borderRadius: 12, border: on ? '2px solid var(--hj-ink)' : '1.5px solid var(--hj-ink-4, #d6d2c8)', background: on ? 'var(--hj-yellow-tint)' : '#fff', color: INK, cursor: 'pointer' }}>
                <span aria-hidden="true" style={{ flex: 'none', width: 18, height: 18, borderRadius: 999, border: '2px solid var(--hj-ink)', background: on ? 'var(--hj-ink)' : 'transparent', boxShadow: on ? 'inset 0 0 0 3px #fff' : 'none' }} />
                <span className="flex flex-col" style={{ gap: 2 }}>
                  <span style={{ fontWeight: 700, fontSize: 16 }}>{choiceTitle(c)}</span>
                  <span style={{ fontSize: 14, color: 'var(--hj-ink-2)' }}>{choiceNote(c)}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
      {canTrial && (
        <button type="button" onClick={() => start(true)} disabled={off} style={disabled({ ...primary, minHeight: 52, fontSize: 16 }, off)}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Start my ${chosen.trialDays}-day free trial`}
        </button>
      )}
      <button type="button" onClick={data.enabled ? claim : () => start(false)} disabled={off} style={disabled(canTrial ? { ...secondary, minHeight: 48, fontSize: 15 } : { ...primary, minHeight: 52, fontSize: 16 }, off)}>
        {busy && !canTrial ? <Loader2 className="w-4 h-4 animate-spin" /> : data.enabled ? 'Claim this address' : offerButton(offer, chosen)}
      </button>
      {err && <p style={errText}>{err} {/card/i.test(err) && <Link to="/billing">Go to Billing</Link>}</p>}
      <p style={{ ...small, fontSize: 13 }}>{offerFinePrint(offer, data.enabled, chosen, canTrial)}</p>
    </div>
  );
};

function choiceTitle(c: HostingChoice) {
  if (c.id === 'standalone') return `Hosting only, ${price(c)}`;
  if (c.id === 'plus_bundle') return `Plus with hosting, ${price(c)}`;
  return price(c);
}
function choiceNote(c: HostingChoice) {
  if (c.id === 'standalone') return 'No membership needed.';
  if (c.id === 'plus_bundle') return 'Everything in Hey Joe Plus, and your site.';
  if (c.id === 'year_item') return 'Added to your yearly Plus bill.';
  if (c.id === 'month_item') return 'Added to your Plus bill.';
  return 'Billed monthly, on its own.';
}
// 30 days reads as "a month"; longer grants (the 6-month class) as months.
function freeLabel(days?: number) {
  const m = Math.max(1, Math.round((days || 30) / 30));
  return m === 1 ? 'A free month' : `${m} free months`;
}
function offerButton(o: HostingOffer, c?: HostingChoice) {
  if (o.kind === 'grant_free') return `Start my ${freeLabel(o.freeDays).replace(/^A /, '')}`;
  if (o.kind === 'charter_free') return 'Start my free year';
  if (o.kind === 'yearly_free') return 'Start my free hosting';
  if (o.kind === 'join_plus') return 'See Plus';
  if (o.freeDays) return `Start my ${Math.round(o.freeDays / 30)} free months`;
  if (c && (c.trialDays > 0)) return `Start now, ${price(c)}`;
  return `Start hosting, ${price(c || o)}`;
}
function offerFinePrint(o: HostingOffer, enabled: boolean, c?: HostingChoice, trial?: boolean) {
  if (enabled) return 'Have your own domain? Connect it once you’re set up.';
  if (o.kind === 'grant_free') return `No card needed. If you don’t add a card in ${o.freeDays || 30} days, it just expires. Your files stay put.`;
  if (o.kind === 'charter_free') return 'Free for a year for charter members. Cancel any time and your files stay put.';
  if (o.kind === 'yearly_free') return `Free for yearly members until your membership renews${o.freeUntil ? ` on ${new Date(o.freeUntil).toLocaleDateString()}` : ''}. Cancel any time and your files stay put.`;
  const p = c || o;
  const free = o.freeDays ? `Free for ${Math.round(o.freeDays / 30)} months, then ${price(p)}. ` : '';
  const tr = trial && c ? `The free trial is ${c.trialDays} days, then ${price(c)}. We take your card now, and you won’t be charged if you cancel before it ends. ` : '';
  if (c?.id === 'year_item' || c?.id === 'month_item') return `${free}${tr}${trial ? 'Starting now adds it to your Plus bill, on the card you already use.' : 'Added to your Plus membership, on the card you already use.'} Cancel any time and your files stay put.`;
  return `${free}${tr}Cancel any time and your files stay put.`;
}

/* ---------- Put the site on the Hey Joe casting profile ---------- */

// One button: the site's address goes onto the member's casting profile as its
// website, next to Instagram and TikTok, where casting sees it (Joe, Oct 25 2026).
const ProfileLinkButton: React.FC<{ site: Site }> = ({ site }) => {
  const [state, setState] = useState<'idle' | 'busy' | 'done'>('idle');
  const [err, setErr] = useState<string | null>(null);
  const go = async () => {
    setState('busy'); setErr(null);
    try { await linkSiteToProfile(site.id); setState('done'); }
    catch (e) { setErr((e as Error).message); setState('idle'); }
  };
  return (
    <span className="inline-flex flex-col" style={{ gap: 4 }}>
      <button type="button" onClick={go} disabled={state !== 'idle'} style={disabled(secondary, state !== 'idle')}>
        {state === 'busy' ? <Loader2 className="w-4 h-4 animate-spin" /> : state === 'done' ? <><Check size={16} aria-hidden="true" /> On your Hey Joe profile</> : 'Add to my Hey Joe profile'}
      </button>
      {err && <span style={{ fontSize: 12.5, color: 'var(--hj-pink-ink)' }}>{err}</span>}
    </span>
  );
};

/* ---------- Home base ---------- */

const HomeBase: React.FC<{ data: MySite; site: Site; onSite: (s: Site) => void; onUpdate: () => void; onError: (e: string | null) => void; onReload: () => void }> = ({ data, site, onSite, onUpdate, onError, onReload }) => {
  const siteType: SiteType = site.siteType || data.siteType || 'acting';
  const [domains, setDomains] = useState<SiteDomain[] | null>(null);
  const [msgs, setMsgs] = useState<SiteMessages | null>(null);
  const [unread, setUnread] = useState(0);
  // Back from connecting GitHub (?github=...): open Settings, where that panel is.
  const [settingsOpen, setSettingsOpen] = useState(() => new URLSearchParams(window.location.search).has('github'));
  const [draftAsked, setDraftAsked] = useState(false);
  const loadDomains = () => fetchDomains(site.id).then((d) => setDomains(d.domains)).catch(() => setDomains([]));
  useEffect(() => { loadDomains(); const t = setInterval(loadDomains, 60000); return () => clearInterval(t); }, [site.id]);
  useEffect(() => {
    fetchSiteMessages(site.id).then((d) => {
      setMsgs(d); const n = d.messages.filter((m) => !m.read).length; setUnread(n);
      if (n) markSiteMessagesRead(site.id).catch(() => {});
    }).catch(() => {});
  }, [site.id]);

  const handleHost = site.url.replace('https://', '');
  const address = site.primaryHost || handleHost;
  const otherHosts = [handleHost, ...(domains || []).filter((d) => d.status === 'live').map((d) => d.domain)].filter((h) => h !== address);
  const isLive = site.status === 'live';
  // An unpublished draft from the Designer shows up top too, reloading as it changes.
  const draftUrl = site.previewUrl && site.previewVersionId && site.previewVersionId !== site.liveVersionId
    ? `${site.previewUrl}${site.previewUrl.includes('?') ? '&' : '?'}hjv=${site.previewVersionId}` : null;
  const pending = site.versions.find((v) => v.id === site.previewVersionId && v.id !== site.liveVersionId);
  const inReview = site.versions.find((v) => v.status === 'review');
  const billing = data.billing;
  const resume = () => startHosting().then((r) => { if (r.checkoutUrl) window.location.href = r.checkoutUrl; else onReload(); }).catch((x) => onError(x.message));

  return (
    <div className="flex flex-col" style={{ gap: 24 }}>
      <section className="flex flex-wrap items-center" style={{ background: 'var(--hj-beige)', color: 'var(--hj-beige-ink)', borderRadius: 20, padding: 'clamp(20px, 3.5vw, 36px)', gap: 32 }}>
        <div className="flex flex-col" style={{ flex: '1 1 400px', minWidth: 0, gap: 12 }}>
          <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
            {isLive ? <span style={pill(LIVE_BG, LIVE_FG)}><span style={{ width: 8, height: 8, borderRadius: 4, background: '#4c9a2a' }} />Live</span>
              : site.status === 'unavailable' ? <span style={pill('var(--hj-yellow-tint)', INK)}>Offline</span>
              : site.status === 'takedown' ? <span style={pill('var(--hj-pink-tint)', 'var(--hj-pink-ink)')}>Taken down</span>
              : <span style={pill('var(--hj-surface-2)', INK)}>Not published yet</span>}
            {site.publishedAt && <span style={{ fontSize: 14, color: 'var(--hj-beige-ink-3)' }}>Updated {day(site.publishedAt)}</span>}
          </div>
          <h1 className="font-hj-display m-0" style={{ fontWeight: 800, fontSize: 'clamp(30px, 5vw, 44px)', lineHeight: 1.05, letterSpacing: '-0.03em', wordBreak: 'break-word' }}>{address}</h1>
          {otherHosts.length > 0 && <div style={{ fontSize: 15, color: 'var(--hj-beige-ink-2)', wordBreak: 'break-word' }}>{otherHosts.join(' and ')} {otherHosts.length === 1 ? 'forwards' : 'forward'} here</div>}
          {isLive && data.usage && (
            <div style={{ fontSize: 15, color: 'var(--hj-beige-ink-2)' }}>
              {(data.usage.requests || 0) > 0 && <>This month: {(data.usage.pages || 0).toLocaleString()} {data.usage.pages === 1 ? 'page view' : 'page views'}, {served(data.usage.bytes || 0)}{data.usage.bandwidthLimitBytes ? ` of ${served(data.usage.bandwidthLimitBytes)}` : ''} served · </>}
              Site size: {size(data.usage.siteBytes)} of {size(data.usage.siteLimitBytes)}
            </div>
          )}
          <div className="flex flex-wrap" style={{ gap: 12, marginTop: 6 }}>
            {site.liveVersionId && <button type="button" onClick={onUpdate} style={secondary}><Upload size={18} aria-hidden="true" />Upload a new version</button>}
            {isLive && <a href={`https://${address}`} target="_blank" rel="noreferrer" style={secondary}>Visit site <ExternalLink size={16} aria-hidden="true" /></a>}
            <button type="button" onClick={() => setSettingsOpen(true)} style={secondary}><SlidersHorizontal size={18} aria-hidden="true" /> Settings</button>
          </div>
        </div>
        <SitePicture url={draftUrl || (isLive ? `https://${address}` : null)} address={address} draft={!!draftUrl} />
      </section>

      {billing.status === 'past_due' && (
        <Banner tone="pink">Your card didn’t go through. Your site stays up for 14 days from the failed payment while you <Link to="/billing" style={{ color: 'inherit', fontWeight: 600 }}>update your card</Link>.</Banner>
      )}
      <BandwidthBanner data={data} onReload={onReload} onError={onError} />
      {site.status === 'unavailable' && !data.bandwidth?.suspended && (
        <Banner tone="yellow" action={<button type="button" onClick={resume} style={linkBtn}>Turn hosting back on</button>}>Your site is showing a “temporarily unavailable” page because hosting isn’t on your account. Your files and address are kept.</Banner>
      )}
      {pending && pending.status === 'ready' && (
        <Banner tone="yellow" action={<button type="button" onClick={onUpdate} style={linkBtn}>Look it over</button>}>You have an update waiting to publish.</Banner>
      )}
      {inReview && <Banner tone="yellow">Your latest upload is getting a quick review, usually because of a password field, a redirect or scrambled code. We’ll email you when it’s ready.</Banner>}
      {unread > 0 && (
        <Banner tone="yellow" action={<a href="#messages" style={linkBtn}>Read {unread === 1 ? 'it' : 'them'}</a>}>
          <Mail size={18} aria-hidden="true" style={{ display: 'inline', marginRight: 8, verticalAlign: '-3px' }} />{unread} new {unread === 1 ? 'message' : 'messages'} from your contact form
        </Banner>
      )}

      {/* Just moved and still plain, or a missing-image to-do from a move: handle it above the Designer. */}
      <SitePhotoTodo site={site} onSite={onSite} />

      {/* The conversational Designer is the primary My Site surface: chat on the left,
          live preview on the right. Videos, Store, Domains, hosting and the rest live in
          the Settings drawer, reached from the header (Joe, Oct 28 2026).
          A brand-new site waits to be asked: nothing builds until they pick "Build a
          first draft" (it costs one of the day's builds, and a client account has no
          profile to draft from). Nothing goes live until Publish (Joe, Nov 2 2026). */}
      {(site.previewVersionId || site.liveVersionId)
        ? <SiteDesigner site={site} onSite={onSite} />
        : (siteType === 'acting' || siteType === 'agency')
          ? (draftAsked
            ? <SitePromptBuilder site={site} onSite={onSite} autoStart compact siteType={siteType} />
            : (
              <section className="flex flex-col" style={{ ...panel, padding: 'clamp(20px, 3vw, 28px)', gap: 14 }}>
                <h2 style={h2}>How do you want to start?</h2>
                <p style={body}>Nothing goes live until you publish.</p>
                <div className="flex flex-wrap" style={{ gap: 12 }}>
                  <button type="button" onClick={() => setDraftAsked(true)} style={primary}>Build a first draft from my Hey Joe profile</button>
                  <button type="button" onClick={onUpdate} style={secondary}><Upload size={18} aria-hidden="true" />Move or upload my site</button>
                </div>
              </section>
            ))
          // Company sites have no Hey Joe profile to build from: they describe it first.
          : <SitePromptBuilder site={site} onSite={onSite} siteType={siteType} />}

      {!site.liveVersionId && (site.previewVersionId || draftAsked || !(siteType === 'acting' || siteType === 'agency')) && (
        <p style={{ ...small, textAlign: 'center' }}>
          Already have a site? <button type="button" onClick={onUpdate} style={{ ...linkBtn, fontSize: 'inherit' }}>Move or upload it instead</button>
        </p>
      )}

      <Sheet open={settingsOpen} onOpenChange={setSettingsOpen}>
        <SheetContent side="right" className="w-full sm:!max-w-[560px]" style={{ background: 'var(--hj-bg)' }}>
          <div className="flex flex-col" style={{ gap: 20, paddingBottom: 24 }}>
            <div className="flex flex-col" style={{ gap: 4, paddingRight: 32 }}>
              <h2 style={{ ...h2, fontSize: 24 }}>Site settings</h2>
              <p style={small}>Everything about your site that isn’t the design itself. Your contact form, videos, store, domains, versions and plan.</p>
            </div>
            {isLive && (
              <div className="flex flex-col" style={{ ...panel, gap: 10 }}>
                <h2 style={{ ...h2, fontSize: 18 }}>Your Hey Joe profile</h2>
                <p style={body}>Put your site’s address on your casting profile, next to Instagram and TikTok.</p>
                <ProfileLinkButton site={site} />
              </div>
            )}
            <DomainPanel site={site} list={domains} setList={setDomains} reload={loadDomains} onSite={onSite} />
            <SiteSharePanel site={site} />
            <SiteSearchPanel site={site} onSite={onSite} />
            <SiteGithubPanel site={site} onSite={onSite} />
            <MessagesPanel site={site} data={msgs} setData={setMsgs} />
            <SiteVideosPanel site={site} />
            <SiteStorePanel />
            <SitePostsPanel site={site} />
            <SiteAgencyPanel />
            <SiteMediaLinks />
            <SitePromptBuilder site={site} onSite={onSite} />
            <VersionsPanel site={site} onSite={onSite} onError={onError} />
            {['active', 'past_due', 'included'].includes(billing.status) && <PlanPanel billing={billing} offer={data.offer} onStopped={onReload} addons={data.addons} onChanged={onReload} />}
            <SiteSettingsPanel site={site} siteType={siteType} onSite={onSite} onDeleted={onReload} />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

// A live, shrunken look at the home page. Scripts run (some sites draw with them) but
// the frame is another origin, can't be clicked, and is skipped by keyboard and readers.
const SitePicture: React.FC<{ url: string | null; address: string; draft?: boolean }> = ({ url, address, draft }) => (
  <div aria-hidden="true" style={{ flex: '1 1 340px', minWidth: 0, maxWidth: 520, background: '#fff', borderRadius: 14, overflow: 'hidden', boxShadow: '0 12px 32px rgba(40,36,30,0.12)' }}>
    <div className="flex items-center" style={{ gap: 6, padding: '10px 12px', background: 'var(--hj-beige-deep)' }}>
      {[0, 1, 2].map((i) => <span key={i} style={{ flex: 'none', width: 10, height: 10, borderRadius: 5, background: 'oklch(0.85 0.01 80)' }} />)}
      <span style={{ marginLeft: 10, font: '12px var(--hj-font-mono)', color: 'var(--hj-ink-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{address}</span>
      {draft && <span style={{ marginLeft: 'auto', flex: 'none', fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: 'var(--hj-yellow-tint)', color: INK }}>Draft</span>}
    </div>
    <div style={{ position: 'relative', height: 240, overflow: 'hidden', background: 'var(--hj-surface-1)' }}>
      {url ? (
        <iframe src={url} title="" tabIndex={-1} loading="lazy" sandbox="allow-scripts"
          style={{ position: 'absolute', top: 0, left: 0, width: '300%', height: 720, border: 0, transform: 'scale(0.3334)', transformOrigin: '0 0', pointerEvents: 'none', background: '#fff' }} />
      ) : (
        <div className="flex items-center justify-center text-center" style={{ height: '100%', padding: 16, color: 'var(--hj-ink-3)', fontSize: 14 }}>Your site shows here once it’s published</div>
      )}
    </div>
  </div>
);

// Free "Meet Hey Joe Sites" class (6ad5926ef418ba28caad6bbf), Tue Oct 27 4:00 PM PT.
// Before it ends: the invite (Joe's copy, Oct 22). After: the same short link, which
// opens the class page with its replay.
const TUTORIAL_ENDS_AT = Date.parse('2026-10-28T01:00:00Z'); // 6 PM PT, an hour after it ends
const TutorialBanner: React.FC = () => {
  const replay = Date.now() >= TUTORIAL_ENDS_AT;
  return (
    <Banner tone="yellow" action={<a href="/w/acting-website" style={linkBtn}>{replay ? 'Watch the replay' : 'Save my seat'}</a>}>
      {replay
        ? 'Need help getting started? Watch the replay of our free tutorial and start saving on hosting.'
        : 'Need help getting started? Join us Oct. 27th for a free tutorial and start saving on hosting.'}
    </Banner>
  );
};

const Banner: React.FC<{ tone: 'yellow' | 'pink'; action?: React.ReactNode; children: React.ReactNode }> = ({ tone, action, children }) => (
  <div className="flex flex-wrap items-center justify-between" role="status" style={{
    gap: 12, padding: '16px 20px', borderRadius: 14, fontSize: 15, fontWeight: 500, lineHeight: 1.45,
    background: tone === 'pink' ? 'var(--hj-pink-tint)' : 'var(--hj-yellow-tint)',
    border: `1px solid ${tone === 'pink' ? 'var(--hj-pink)' : 'var(--hj-yellow)'}`,
    color: tone === 'pink' ? 'var(--hj-pink-ink)' : INK,
  }}>
    <span style={{ flex: '1 1 260px', minWidth: 0 }}>{children}</span>{action}
  </div>
);

const MessagesPanel: React.FC<{ site: Site; data: SiteMessages | null; setData: (d: SiteMessages | null) => void }> = ({ site, data, setData }) => {
  const [showAll, setShowAll] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [saved, setSaved] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [howTo, setHowTo] = useState(false);
  useEffect(() => { setEmail(data?.notifyEmail || ''); }, [data?.notifyEmail]);
  const list = data ? (showAll ? data.messages : data.messages.slice(0, 5)) : [];
  const preview = (f: Record<string, string>) => f.message || f.Message || Object.entries(f).filter(([k]) => !/^(name|email)$/i.test(k)).map(([, v]) => v).join(' · ');
  return (
    <section id="messages" className="flex flex-col" style={{ ...panel, padding: 'clamp(20px, 3vw, 28px)', gap: 4, scrollMarginTop: 80 }}>
      <div className="flex flex-wrap items-baseline justify-between" style={{ gap: 12, marginBottom: 10 }}>
        <h2 style={{ ...h2, fontSize: 22 }}>Messages</h2>
        {data?.notifyEmail && <span style={{ ...small, wordBreak: 'break-all' }}>Also sent to {data.notifyEmail}</span>}
      </div>
      {data?.notifyProblem && (
        <Banner tone="pink">We couldn’t reach {data.notifyProblem.email}, so some notices didn’t arrive. Every message is here. Check the address below.</Banner>
      )}
      {!data ? <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--hj-ink-3)' }} />
        : data.messages.length === 0 ? <p style={{ ...body, padding: '12px 0' }}>No messages yet. When someone uses the contact form on your site, it shows up here and in your email.</p>
        : list.map((m) => (
          <div key={m.id} className="flex" style={{ gap: 14, padding: '16px 0', borderTop: '1px solid var(--hj-hairline-soft)' }}>
            <span aria-hidden="true" style={{ flex: 'none', width: 8, height: 8, borderRadius: 4, marginTop: 8, background: m.read ? 'transparent' : INK }} />
            <div className="flex flex-col" style={{ flex: 1, minWidth: 0, gap: 4 }}>
              <div className="flex justify-between" style={{ gap: 12 }}>
                <span style={{ fontWeight: m.read ? 500 : 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.fields.name || m.replyTo || 'Message'}</span>
                <span style={{ fontSize: 13, color: MUTED, flex: 'none' }}>{when(m.createdAt)}</span>
              </div>
              {open === m.id ? (
                <div className="flex flex-col" style={{ gap: 4 }}>
                  {Object.entries(m.fields).filter(([k]) => k !== 'name').map(([k, v]) => (
                    <p key={k} style={{ ...body, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}><span style={{ color: 'var(--hj-ink-3)' }}>{k}: </span>{v}</p>
                  ))}
                </div>
              ) : (
                <p style={{ ...body, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' }}>{preview(m.fields)}</p>
              )}
              <div className="flex flex-wrap items-center" style={{ gap: 16, marginTop: 2 }}>
                {m.replyTo && <a href={`mailto:${m.replyTo}`} style={{ fontSize: 14, fontWeight: 600, color: INK, minHeight: 32, display: 'inline-flex', alignItems: 'center' }}>Reply</a>}
                {open !== m.id && <button type="button" onClick={() => setOpen(m.id)} style={{ ...linkBtn, fontWeight: 500, textDecoration: 'none', color: MUTED }}>Show all</button>}
                {m.delivered === 'bounced' && <span style={{ fontSize: 13, color: 'var(--hj-pink-ink)' }}>Email notice bounced</span>}
              </div>
            </div>
          </div>
        ))}
      {data && data.messages.length > 5 && !showAll && <button type="button" onClick={() => setShowAll(true)} style={{ ...linkBtn, alignSelf: 'flex-start', marginTop: 8 }}>See all {data.messages.length} messages</button>}

      <div className="flex flex-col" style={{ marginTop: 18, paddingTop: 18, borderTop: '1px solid var(--hj-hairline-soft)', gap: 10 }}>
        <label htmlFor="notify-email" style={{ fontWeight: 600, fontSize: 15 }}>Send messages to</label>
        <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
          <input id="notify-email" type="email" value={email} onChange={(e) => { setEmail(e.target.value); setSaved(null); }}
            style={{ flex: '1 1 220px', minWidth: 0, maxWidth: 320, minHeight: 44, border: '1px solid var(--hj-hairline)', borderRadius: 10, padding: '0 12px', font: '15px var(--hj-font-ui)', color: INK, background: '#fff' }} />
          <button type="button" disabled={!email || email === data?.notifyEmail} style={disabled(quiet, !email || email === data?.notifyEmail)} onClick={async () => {
            try { const r = await saveNotifyEmail(site.id, email); if (data) setData({ ...data, notifyEmail: r.notifyEmail, notifyProblem: null }); setSaved('Saved.'); } catch (e) { setSaved((e as Error).message); }
          }}>Save</button>
          {saved && <span style={small}>{saved}</span>}
        </div>
        <div className="flex flex-col" style={{ marginTop: 8, paddingTop: 14, borderTop: '1px solid var(--hj-hairline-soft)', gap: 8 }}>
          <span style={{ fontWeight: 600, fontSize: 15 }}>Your contact form</span>
          <p style={{ ...small, margin: 0 }}>Customize the form however you like: change the style, edit labels, or add fields. All responses arrive with the message.</p>
          <button type="button" aria-expanded={howTo} onClick={() => setHowTo(!howTo)} style={{ ...linkBtn, alignSelf: 'flex-start' }}>{howTo ? 'Hide form code' : 'Show form code'}</button>
          {howTo && (
            <div className="flex flex-col" style={{ gap: 10 }}>
              <pre style={{ background: 'var(--hj-surface-1)', borderRadius: 10, padding: 12, fontSize: 12, overflowX: 'auto', whiteSpace: 'pre', margin: 0 }}>{site.formSnippet}</pre>
              <button type="button" style={{ ...quiet, alignSelf: 'flex-start' }} onClick={() => { navigator.clipboard?.writeText(site.formSnippet).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }); }}>
                <Copy size={14} aria-hidden="true" /> {copied ? 'Copied' : 'Copy form'}
              </button>
              <p style={{ ...small, margin: 0 }}>Paste it into your site’s HTML where the form should go. Three things to keep: the <code>action</code> address, a field named <code>email</code> (that’s what lets you hit Reply), and the hidden <code>_hp</code> line, which catches bots. Everything else is up to you, up to 20 questions.</p>
              <p style={{ ...small, margin: 0 }}>After someone sends a message they see a short thank-you page. To use your own, add <code>{`<input type="hidden" name="_next" value="https://${site.primaryHost || site.url.replace('https://', '')}/thanks.html">`}</code> inside the form, pointing at a page on your site.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const DOMAIN_STATUS: Record<SiteDomain['status'], string> = {
  pending_dns: 'Waiting for your DNS record',
  misconfigured: 'Not pointed at us yet',
  issuing: 'Connected. Setting up the secure certificate (usually under 30 minutes)',
  live: 'Connected and secure (https). Renews on its own.',
  expired: 'We stopped checking. Add the record below, then reload this page.',
};
const DomainPanel: React.FC<{ site: Site; list: SiteDomain[] | null; setList: (d: SiteDomain[]) => void; reload: () => void; onSite: (s: Site) => void }> = ({ site, list, setList, reload, onSite }) => {
  const [domain, setDomain] = useState('');
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mainBusy, setMainBusy] = useState<string | null>(null);
  const showForm = adding || (list !== null && list.length === 0);
  const handleHost = site.url.replace('https://', '');
  const liveHosts = [handleHost, ...(list || []).filter((d) => d.status === 'live').map((d) => d.domain)];
  const main = site.primaryHost || handleHost;
  const makeMain = async (host: string) => {
    setMainBusy(host); setErr(null);
    try { const r = await setPrimaryHost(site.id, host); onSite({ ...site, primaryHost: r.primaryHost }); } catch (e) { setErr((e as Error).message); }
    setMainBusy(null);
  };
  const mainControl = (host: string) => (liveHosts.length < 2 ? null : host === main
    ? <span style={{ ...pill(LIVE_BG, LIVE_FG), fontSize: 12, padding: '3px 8px', alignSelf: 'flex-start' }}>Main address</span>
    : (
      <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
        <span style={small}>Forwards to {main}</span>
        <button type="button" disabled={!!mainBusy} onClick={() => makeMain(host)} style={disabled({ ...linkBtn, fontSize: 13 }, !!mainBusy)}>{mainBusy === host ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Make this the main address'}</button>
      </div>
    ));
  return (
    <section className="flex flex-col" style={{ ...panel, gap: 10 }}>
      <h2 style={{ ...h2, fontSize: 18 }}>Your addresses</h2>
      {liveHosts.length > 1 && (
        <div className="flex flex-col" style={{ gap: 6 }}>
          <div style={{ fontWeight: 600, wordBreak: 'break-all' }}>{handleHost}</div>
          {mainControl(handleHost)}
        </div>
      )}
      {list?.map((d) => (
        <div key={d.domain} className="flex flex-col" style={{ gap: 6 }}>
          <div className="flex items-center" style={{ gap: 8, fontWeight: 600, wordBreak: 'break-all' }}>
            {d.status === 'live' ? <Check size={16} strokeWidth={2.5} color={LIVE_FG} aria-hidden="true" style={{ flex: 'none' }} /> : <AlertCircle size={16} aria-hidden="true" style={{ flex: 'none' }} color={d.status === 'misconfigured' ? 'var(--hj-pink-ink)' : 'var(--hj-ink-3)'} />}
            {d.domain}
          </div>
          <p style={{ ...small, color: d.status === 'misconfigured' || d.status === 'expired' ? 'var(--hj-pink-ink)' : d.status === 'live' ? LIVE_FG : MUTED }}>{DOMAIN_STATUS[d.status]}</p>
          {d.status === 'live' && mainControl(d.domain)}
          {d.status !== 'live' && (
            <div style={{ background: 'var(--hj-surface-1)', borderRadius: 10, padding: 12, fontSize: 13 }}>
              <p style={{ margin: '0 0 6px' }}>At your domain company, add this record:</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', wordBreak: 'break-all' }}>
                <span style={{ color: 'var(--hj-ink-3)' }}>Type</span><span>{d.apex ? 'ALIAS, ANAME or CNAME (flattened)' : 'CNAME'}</span>
                <span style={{ color: 'var(--hj-ink-3)' }}>Name</span><span>{d.domain.split('.').length > 2 && !d.apex ? d.domain.split('.')[0] : '@'}</span>
                <span style={{ color: 'var(--hj-ink-3)' }}>Value</span><span>{d.record.value}</span>
              </div>
              {d.ownership && (
                <>
                  <p style={{ margin: '10px 0 6px' }}>And this one, which lets CloudFront confirm the bare domain is yours:</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', wordBreak: 'break-all' }}>
                    <span style={{ color: 'var(--hj-ink-3)' }}>Type</span><span>{d.ownership.type}</span>
                    <span style={{ color: 'var(--hj-ink-3)' }}>Name</span><span>{d.ownership.host}</span>
                    <span style={{ color: 'var(--hj-ink-3)' }}>Value</span><span>{d.ownership.value}</span>
                  </div>
                </>
              )}
              {d.apex && d.companionOf && <p style={{ margin: '8px 0 0' }}>We connected this with {d.companionOf} so {d.domain} works without the www. If your domain company offers ALIAS, ANAME or CNAME flattening, add the record above. If it doesn’t, turn on its free forwarding from {d.domain} to {d.companionOf} and disconnect this one.</p>}
              {d.apex && !d.companionOf && <p style={{ margin: '8px 0 0' }}>This is a bare domain. Many domain companies can’t point one. If yours offers ALIAS, ANAME or CNAME flattening, add the record above. If not, turn on its free forwarding to www.{d.domain}.</p>}
              {!d.apex && d.companionOf && <p style={{ margin: '8px 0 0' }}>We connected this with {d.companionOf} so the site answers with or without the www.</p>}
              {d.problem && <p style={{ margin: '8px 0 0', color: 'var(--hj-pink-ink)' }}>{d.problem}</p>}
            </div>
          )}
          <button type="button" onClick={async () => { await removeDomain(site.id, d.domain).catch(() => {}); reload(); }} style={{ ...linkBtn, alignSelf: 'flex-start', fontWeight: 500, color: MUTED, fontSize: 13 }}>Disconnect</button>
        </div>
      ))}
      {showForm ? (
        <div className="flex flex-col" style={{ gap: 8 }}>
          <p style={small}>Already own a domain? Type it either way, www.janedoe.com or janedoe.com. We connect both, so the site answers with or without the www, and one forwards to the other.</p>
          <div className="flex flex-wrap" style={{ gap: 8 }}>
            <input aria-label="Your domain" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="www.janedoe.com" autoCapitalize="none" spellCheck={false}
              style={{ flex: '1 1 180px', minWidth: 0, minHeight: 44, border: '1px solid var(--hj-hairline)', borderRadius: 10, padding: '0 12px', font: '15px var(--hj-font-ui)', color: INK, background: '#fff' }} />
            <button type="button" disabled={!domain || busy} style={disabled(quiet, !domain || busy)} onClick={async () => {
              setBusy(true); setErr(null);
              try { const r = await addDomain(site.id, domain); setList(r.domains); setDomain(''); setAdding(false); } catch (e) { setErr((e as Error).message); }
              setBusy(false);
            }}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Connect'}</button>
          </div>
          {err && <p style={errText}>{err}</p>}
        </div>
      ) : list && <>
        {err && <p style={errText}>{err}</p>}
        <button type="button" onClick={() => setAdding(true)} style={{ ...linkBtn, alignSelf: 'flex-start' }}>Connect another domain</button>
      </>}
    </section>
  );
};

// Change the heyjoesites.com address, or delete the site for good.
const SiteSettingsPanel: React.FC<{ site: Site; siteType: SiteType; onSite: (s: Site) => void; onDeleted: () => void }> = ({ site, siteType, onSite, onDeleted }) => {
  const [mode, setMode] = useState<'none' | 'rename' | 'delete'>('none');
  const [handle, setHandle] = useState('');
  const [check, setCheck] = useState<{ ok: boolean; problem?: string } | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  useEffect(() => {
    if (mode !== 'rename' || !handle || handle === site.handle) { setCheck(null); return; }
    const t = setTimeout(() => { checkHandle(handle).then(setCheck).catch(() => setCheck(null)); }, 300);
    return () => clearTimeout(t);
  }, [handle, mode, site.handle]);
  const input: React.CSSProperties = { flex: '1 1 160px', minWidth: 0, minHeight: 44, border: '1px solid var(--hj-hairline)', borderRadius: 10, padding: '0 12px', font: '15px var(--hj-font-ui)', color: INK, background: '#fff' };
  const old = site.oldHandles[site.oldHandles.length - 1];
  return (
    <section className="flex flex-col" style={{ ...panel, gap: 10 }}>
      <h2 style={{ ...h2, fontSize: 18 }}>Site settings</h2>
      {done && <p style={{ ...small, color: LIVE_FG }}>{done}</p>}
      {old && !done && <p style={small}>{old.handle}.{site.url.split('.').slice(1).join('.')} forwards here until {day(old.until)}.</p>}
      <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
        <label htmlFor="site-kind" style={{ fontWeight: 600, fontSize: 14 }}>Kind of site</label>
        <select id="site-kind" value={siteType} disabled={busy} style={{ ...input, flex: '1 1 200px' }} onChange={async (e) => {
          const t = e.target.value as SiteType;
          setBusy(true); setErr(null); setDone(null);
          try { await saveSiteType(t); onSite({ ...site, siteType: t }); setDone('Saved. The designer now works on it as that kind of site.'); } catch (x) { setErr((x as Error).message); }
          setBusy(false);
        }}>
          {SITE_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
      </div>
      {mode === 'none' && err && <p style={errText}>{err}</p>}
      {mode === 'none' && (
        <div className="flex flex-wrap" style={{ gap: 16 }}>
          <button type="button" onClick={() => { setMode('rename'); setHandle(site.handle); setErr(null); setDone(null); }} style={{ ...linkBtn, fontWeight: 500 }}>Change my address</button>
          <button type="button" onClick={() => { setMode('delete'); setConfirmText(''); setErr(null); setDone(null); }} style={{ ...linkBtn, fontWeight: 500, color: MUTED }}>Delete my site</button>
        </div>
      )}
      {mode === 'rename' && (
        <div className="flex flex-col" style={{ gap: 8 }}>
          <label htmlFor="site-rename" style={{ fontWeight: 600, fontSize: 14 }}>New address</label>
          <div className="flex flex-wrap items-center" style={{ gap: 6 }}>
            <input id="site-rename" value={handle} autoComplete="off" autoCapitalize="none" spellCheck={false} style={input}
              onChange={(e) => setHandle(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} />
            <span style={{ ...small, wordBreak: 'break-all' }}>.{site.url.split('.').slice(1).join('.')}</span>
          </div>
          {check && !check.ok && <p style={errText}>{check.problem}</p>}
          <p style={small}>Your old address forwards to your site for 30 days, so links you’ve shared keep working. You can change it once a day.</p>
          {err && <p style={errText}>{err}</p>}
          <div className="flex flex-wrap" style={{ gap: 8 }}>
            <button type="button" disabled={busy} onClick={() => setMode('none')} style={quiet}>Cancel</button>
            <button type="button" disabled={busy || !check?.ok} style={disabled(quiet, busy || !check?.ok)} onClick={async () => {
              setBusy(true); setErr(null);
              try { const r = await renameSite(site.id, handle); onSite(r.site); setMode('none'); setDone(`Your address is now ${r.site.url.replace('https://', '')}.`); } catch (e) { setErr((e as Error).message); }
              setBusy(false);
            }}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Change address'}</button>
          </div>
        </div>
      )}
      {mode === 'delete' && (
        <div className="flex flex-col" style={{ gap: 8 }}>
          <p style={small}>This deletes your site, every saved version and your contact form messages, and disconnects your domains. It can’t be undone. Download your site first if you want a copy. Hosting billing doesn’t change; stop it under Plan.</p>
          <label htmlFor="site-delete" style={{ fontWeight: 600, fontSize: 14 }}>Type {site.handle} to confirm</label>
          <input id="site-delete" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} autoComplete="off" autoCapitalize="none" spellCheck={false} style={{ ...input, flex: 'none' }} />
          {err && <p style={errText}>{err}</p>}
          <div className="flex flex-wrap" style={{ gap: 8 }}>
            <button type="button" disabled={busy} onClick={() => setMode('none')} style={quiet}>Keep my site</button>
            <button type="button" disabled={busy || confirmText.trim().toLowerCase() !== site.handle}
              style={disabled({ ...quiet, background: 'hsl(var(--destructive))', color: 'hsl(var(--destructive-foreground))', border: 0 }, busy || confirmText.trim().toLowerCase() !== site.handle)}
              onClick={async () => {
                setBusy(true); setErr(null);
                try { await deleteSite(site.id, confirmText); onDeleted(); } catch (e) { setErr((e as Error).message); setBusy(false); }
              }}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete my site'}</button>
          </div>
        </div>
      )}
    </section>
  );
};

const VersionsPanel: React.FC<{ site: Site; onSite: (s: Site) => void; onError: (e: string | null) => void }> = ({ site, onSite, onError }) => {
  const [busy, setBusy] = useState(false);
  const live = site.versions.find((v) => v.id === site.liveVersionId);
  const prev = site.versions.find((v) => v.id === site.previousVersionId);
  if (!live && !prev) return null;
  return (
    <section className="flex flex-col" style={{ ...panel, gap: 12 }}>
      <h2 style={{ ...h2, fontSize: 18 }}>Versions</h2>
      {live && (
        <div className="flex items-center justify-between" style={{ gap: 10, fontSize: 14 }}>
          <span><b style={{ fontWeight: 600 }}>{day(site.publishedAt || live.uploadedAt)}</b> <span style={{ color: MUTED }}>· {live.fileCount} files</span></span>
          <span style={{ ...pill(LIVE_BG, LIVE_FG), fontSize: 12, padding: '3px 8px' }}>Live</span>
        </div>
      )}
      {prev && (
        <div className="flex items-center justify-between" style={{ gap: 10, fontSize: 14 }}>
          <span><b style={{ fontWeight: 600 }}>{day(prev.uploadedAt)}</b> <span style={{ color: MUTED }}>· {prev.fileCount} files</span></span>
          <button type="button" disabled={busy} style={disabled(quiet, busy)} onClick={async () => {
            setBusy(true); onError(null);
            try { onSite((await rollbackSite(site.id)).site); } catch (e) { onError((e as Error).message); }
            setBusy(false);
          }}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RotateCcw size={14} aria-hidden="true" /> Go back to this</>}</button>
        </div>
      )}
      {live && (
        <button type="button" style={{ ...linkBtn, alignSelf: 'flex-start' }} onClick={async () => {
          try { window.location.href = (await siteDownloadUrl(site.id)).url; } catch (e) { onError((e as Error).message); }
        }}><Download size={14} aria-hidden="true" style={{ display: 'inline', marginRight: 6, verticalAlign: '-2px' }} />Download my files</button>
      )}
    </section>
  );
};

const PlanPanel: React.FC<{ billing: MySite['billing']; offer: HostingOffer; onStopped: () => void; addons?: SiteAddons; onChanged: () => void }> = ({ billing, offer, onStopped, addons, onChanged }) => {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const line = billing.status === 'included' ? 'Included with your account.'
    : billing.mode === 'subscription' && billing.plan === 'standalone' ? 'Site hosting, $9.99/month.'
    : billing.mode === 'subscription' ? `Site hosting, ${billing.interval === 'year' ? '$50/year' : '$5/month'}, billed on its own.`
    : billing.mode === 'grant_free' ? `Free until ${billing.endsAt ? new Date(billing.endsAt).toLocaleDateString() : 'your free time ends'}. There’s no card on file, so if you don’t add one by then, hosting just ends and your files stay put.`
    : billing.mode === 'comp' ? `Free until ${billing.endsAt ? new Date(billing.endsAt).toLocaleDateString() : 'your Plus renewal'}.`
    : billing.mode === 'charter_free' ? `Free for charter members until ${billing.endsAt ? new Date(billing.endsAt).toLocaleDateString() : 'a year from when you started'}.`
    : `Site hosting, ${billing.interval === 'year' ? '$50/year' : '$5/month'}.`;
  const sub = billing.status !== 'included' && billing.mode === 'item' ? 'On your Plus membership.' : '';
  return (
    <section className="flex flex-col" style={{ ...panel, gap: 8 }}>
      <h2 style={{ ...h2, fontSize: 18 }}>Plan</h2>
      <p style={{ margin: 0, fontSize: 15 }}>{line}</p>
      {(sub || billing.cancelAt) && <p style={small}>{sub}{billing.cancelAt ? ` Ends ${new Date(billing.cancelAt).toLocaleDateString()}.` : ''}</p>}
      {billing.mode === 'grant_free' && <KeepSite offer={offer} />}
      {addons && <AddOns addons={addons} onChanged={onChanged} />}
      {billing.status !== 'included' && !billing.cancelAt && (!confirm ? (
        <button type="button" onClick={() => setConfirm(true)} style={{ ...linkBtn, alignSelf: 'flex-start', fontWeight: 500, color: MUTED }}>Remove hosting</button>
      ) : (
        <div className="flex flex-col" style={{ gap: 10 }}>
          <p style={small}>Your site will show a “temporarily unavailable” page. Your files and address are kept for 90 days, and you can download your site any time.{billing.mode === 'item' ? ' You stay on Plus.' : ''}</p>
          {err && <p style={errText}>{err}</p>}
          <div className="flex flex-wrap" style={{ gap: 8 }}>
            <button type="button" disabled={busy} onClick={() => setConfirm(false)} style={quiet}>Keep hosting</button>
            <button type="button" disabled={busy} style={{ ...quiet, background: 'hsl(var(--destructive))', color: 'hsl(var(--destructive-foreground))', border: 0 }} onClick={async () => {
              setBusy(true); setErr(null);
              try { await stopHosting(); onStopped(); } catch (e) { setErr((e as Error).message); }
              setBusy(false); setConfirm(false);
            }}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Remove hosting'}</button>
          </div>
        </div>
      ))}
    </section>
  );
};

// More storage and bandwidth, a count of each (Joe, Nov 2 2026): +1 GB of storage
// for $3/month, +25 GB of bandwidth a month for $5/month, on the hosting bill.
const dollars = (c: number) => `$${c % 100 ? (c / 100).toFixed(2) : c / 100}`;
const AddOns: React.FC<{ addons: SiteAddons; onChanged: () => void }> = ({ addons, onChanged }) => {
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const change = async (kind: 'storage' | 'bandwidth', to: number) => {
    setBusy(kind); setErr(null);
    try { await setSiteAddon(kind, to); onChanged(); } catch (e) { setErr((e as Error).message); }
    setBusy(null);
  };
  const rows: { kind: 'storage' | 'bandwidth'; title: string; each: string; n: number; max: number; cents: number }[] = [
    { kind: 'storage', title: 'Extra storage', each: '1 GB', n: addons.storage, max: addons.storageMax, cents: addons.storageCents },
    { kind: 'bandwidth', title: 'Extra bandwidth', each: '25 GB a month', n: addons.bandwidth, max: addons.bandwidthMax, cents: addons.bandwidthCents },
  ];
  return (
    <div className="flex flex-col" style={{ gap: 10, borderTop: '1px solid var(--hj-hairline-soft)', paddingTop: 12, marginTop: 4 }}>
      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: INK }}>Add-ons</h3>
      <p style={small}>Hosting includes 250 MB of storage and 10 GB of bandwidth a month.</p>
      {rows.map((r) => (
        <div key={r.kind} className="flex flex-wrap items-center justify-between" style={{ gap: 10 }}>
          <div className="flex flex-col" style={{ minWidth: 0 }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: INK }}>{r.title}: {r.n === 0 ? 'none' : r.kind === 'storage' ? `${r.n} GB` : `${r.n * 25} GB a month`}</span>
            <span style={small}>{dollars(r.cents)}/month for each {r.each}{r.n ? `, ${dollars(r.cents * r.n)}/month now` : ''}</span>
          </div>
          <div className="flex items-center" style={{ gap: 8 }}>
            <button type="button" aria-label={`Less ${r.title.toLowerCase()}`} disabled={!!busy || r.n === 0 || !addons.canBuy} onClick={() => change(r.kind, r.n - 1)} style={disabled({ ...quiet, minWidth: 44 }, !!busy || r.n === 0 || !addons.canBuy)}><Minus size={16} aria-hidden="true" /></button>
            <button type="button" disabled={!!busy || r.n >= r.max || !addons.canBuy} onClick={() => change(r.kind, r.n + 1)} style={disabled(quiet, !!busy || r.n >= r.max || !addons.canBuy)}>
              {busy === r.kind ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus size={16} aria-hidden="true" /> Add {r.each}</>}
            </button>
          </div>
        </div>
      ))}
      {!addons.canBuy && <p style={small}>Add-ons go on a card. Add a card to your hosting first, or email hello@heyjoe.io.</p>}
      {err && <p style={errText}>{err}</p>}
    </div>
  );
};

// Bandwidth: close to the limit, over it, on notice, or suspended (siteHosting checkBandwidth).
const BandwidthBanner: React.FC<{ data: MySite; onReload: () => void; onError: (e: string | null) => void }> = ({ data, onReload, onError }) => {
  const [busy, setBusy] = useState(false);
  const used = data.usage?.bytes || 0; const limit = data.usage?.bandwidthLimitBytes || 0;
  const bw = data.bandwidth; const a = data.addons;
  if (!limit || !bw || !a) return null;
  const add = a.canBuy && a.bandwidth < a.bandwidthMax ? (
    <button type="button" disabled={busy} style={linkBtn} onClick={async () => {
      setBusy(true); onError(null);
      try { await setSiteAddon('bandwidth', a.bandwidth + 1); onReload(); } catch (e) { onError((e as Error).message); }
      setBusy(false);
    }}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Add 25 GB a month for ${dollars(a.bandwidthCents)}`}</button>
  ) : undefined;
  const fmt = (d: string) => new Date(d).toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
  if (bw.suspended) return <Banner tone="pink" action={add}>Your site is showing an account suspended page because it went over its {served(limit)} of bandwidth two months in a row. Add bandwidth and it comes right back.</Banner>;
  if (bw.notice) return <Banner tone="pink" action={add}>Your site went over its bandwidth last month and is over again this month ({served(used)} of {served(limit)}). Add bandwidth by {fmt(bw.notice.suspendAt)}, or it will show an account suspended page until you do.</Banner>;
  if (used > limit) return <Banner tone="yellow" action={add}>Your site has used {served(used)} of bandwidth this month, over the {served(limit)} included. It stays up. If it goes over again next month, you’ll have two weeks to add bandwidth before it’s suspended.</Banner>;
  if (used >= 0.8 * limit) return <Banner tone="yellow" action={add}>Your site has used {served(used)} of its {served(limit)} of bandwidth this month.</Banner>;
  return null;
};

// During free time from a class: add a card to keep the site after it ends. The
// days left carry over, so the first charge lands when the free time would have ended.
const KeepSite: React.FC<{ offer: HostingOffer }> = ({ offer }) => {
  const choices = offer.choices || [];
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState<string | undefined>(choices[0]?.id);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!choices.length) return null;
  const chosen = choices.find((c) => c.id === pick) || choices[0];
  if (!open) return <button type="button" onClick={() => setOpen(true)} style={{ ...secondary, alignSelf: 'flex-start', minHeight: 44, fontSize: 15 }}>Add a card to keep my site</button>;
  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      {choices.length > 1 && (
        <div role="radiogroup" aria-label="Pick a plan" className="flex flex-col" style={{ gap: 8 }}>
          {choices.map((c) => {
            const on = c.id === chosen.id;
            return (
              <button key={c.id} type="button" role="radio" aria-checked={on} onClick={() => setPick(c.id)}
                className="flex items-center text-left" style={{ gap: 12, padding: '12px 14px', borderRadius: 12, border: on ? '2px solid var(--hj-ink)' : '1.5px solid var(--hj-ink-4, #d6d2c8)', background: on ? 'var(--hj-yellow-tint)' : '#fff', color: INK, cursor: 'pointer' }}>
                <span aria-hidden="true" style={{ flex: 'none', width: 18, height: 18, borderRadius: 999, border: '2px solid var(--hj-ink)', background: on ? 'var(--hj-ink)' : 'transparent', boxShadow: on ? 'inset 0 0 0 3px #fff' : 'none' }} />
                <span className="flex flex-col" style={{ gap: 2 }}>
                  <span style={{ fontWeight: 700, fontSize: 16 }}>{choiceTitle(c)}</span>
                  <span style={{ fontSize: 14, color: 'var(--hj-ink-2)' }}>{choiceNote(c)}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
      <p style={small}>{offer.freeUntil ? `Nothing is charged until ${new Date(offer.freeUntil).toLocaleDateString()}, then ${price(chosen)}.` : `${price(chosen)}.`} Cancel any time.</p>
      {err && <p style={errText}>{err} {/card/i.test(err) && <Link to="/billing">Go to Billing</Link>}</p>}
      <button type="button" disabled={busy} style={disabled({ ...primary, alignSelf: 'flex-start', minHeight: 48, fontSize: 15 }, busy)} onClick={async () => {
        setBusy(true); setErr(null);
        try {
          const r = await startHosting(chosen.id, false);
          if (r.checkoutUrl) { window.location.href = r.checkoutUrl; return; }
          window.location.reload();
        } catch (e) { setErr((e as Error).message); setBusy(false); }
      }}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Keep my site'}</button>
    </div>
  );
};

/* ---------- Update my site: upload, check, preview, publish ---------- */

// Move my site: paste the current address, we fetch the pages and files and put the
// result on the preview address like any upload. The report lists what we couldn't get.
const MoveMySite: React.FC<{ site: Site; onSite: (s: Site) => void; initialUrl?: string; onDesign: () => void }> = ({ site, onSite, initialUrl, onDesign }) => {
  const [url, setUrl] = useState(initialUrl || '');
  const [matchOld, setMatchOld] = useState(false);
  const [fit, setFit] = useState<Fit | null>(null);
  const [rights, setRights] = useState(false);
  const [job, setJob] = useState<SiteImport | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const timer = useRef<number | null>(null);
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);
  const poll = (id: string) => {
    timer.current = window.setTimeout(async () => {
      try {
        const r = await getSiteImport(site.id, id); setJob(r.import);
        if (r.import.status === 'running') poll(id);
        else if (r.import.status === 'done') { const fresh = await fetchMySite(); if (fresh.site) onSite(fresh.site); }
      } catch (e) { setErr((e as Error).message); }
    }, 3000);
  };
  const start = async () => {
    setBusy(true); setErr(null); setJob(null);
    try { const r = await startSiteImport(site.id, url); setJob(r.import); poll(r.import.id); } catch (e) { setErr((e as Error).message); }
    setBusy(false);
  };
  const rep = job?.report;
  return (
    <section className="flex flex-col" style={{ ...panel, gap: 14 }}>
      <h2 style={h2}>Or move your current site</h2>
      <p style={body}>Scan your site first. Then we fetch your pages, photos, audio and PDFs, rebuild them as files you own, and put the result on your private preview. Your current site isn’t touched.</p>
      <p style={small}>Depending on your host, we might not be able to download every image or file. We list anything we couldn’t get, so you may need to collect some of it yourself afterwards. Site builders’ templates and scripts are left behind on purpose; your words and pictures come with you.</p>
      {(!job || job.status === 'failed') && (
        <>
          <SiteScan context="move" onFit={(f, u) => { setFit(f); setUrl(u); }} initialUrl={initialUrl} autoRun={!!initialUrl} />
          {fit && (
            <>
              <div className="flex flex-col" style={{ gap: 10, background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', borderRadius: 12, padding: 16 }}>
                <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: INK }}>Confirm ownership and permissions</p>
                <label className="flex items-start" style={{ gap: 10, fontSize: 15, color: INK, cursor: 'pointer' }}>
                  <Checkbox checked={rights} onCheckedChange={(v) => setRights(v === true)} style={{ marginTop: 3 }} />
                  <span>I own this website or am authorized to move it, including its images, videos, and other content.</span>
                </label>
                <p style={small}>Your website&rsquo;s media will be transferred to Hey Joe&rsquo;s hosting storage. It will not be submitted to a generative AI model as part of the import.</p>
              </div>
              <button type="button" disabled={!url || !rights || busy} onClick={start} style={disabled({ ...primary, alignSelf: 'flex-start' }, !url || !rights || busy)}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : fit.verdict === 'later' ? 'Move it anyway' : 'Move my site'}</button>
            </>
          )}
        </>
      )}
      {job?.status === 'running' && <p className="flex items-center" style={{ ...body, gap: 8 }}><Loader2 className="w-4 h-4 animate-spin" /> Fetching {job.url}. This can take a minute or two.</p>}
      {job?.status === 'failed' && <p style={errText}>{job.error}</p>}
      {job?.status === 'done' && rep && (
        <div className="flex flex-col" style={{ gap: 10 }}>
          <p style={{ ...body, fontWeight: 600 }}>Done. {rep.pages.length} {rep.pages.length === 1 ? 'page' : 'pages'} and {rep.saved} {rep.saved === 1 ? 'file' : 'files'} moved{rep.siteName ? ` from ${rep.siteName}` : ''}.{job.held ? ' It’s getting a quick review before the preview.' : ' It’s on your preview address now, in a clean layout.'}</p>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, color: MUTED }}>
            {rep.pages.map((p) => <li key={p.path}>{p.title || p.path} <span style={{ color: 'var(--hj-ink-4)' }}>{p.path}</span></li>)}
          </ul>
          {/* Missing pictures are a to-do with an upload button per photo, not a list to save by hand. */}
          <SitePhotoTodo site={site} onSite={onSite} importJob={job} compact />
          {rep.failed.filter((f) => !((f as { kind?: string }).kind === 'image' || /\.(jpe?g|png|gif|webp|avif|svg)(\?|$)/i.test(f.url))).length > 0 && (
            <div style={{ background: 'var(--hj-surface-1)', borderRadius: 12, padding: 14 }}>
              <p style={{ ...small, color: INK, fontWeight: 600, marginBottom: 6 }}>These other files couldn’t be downloaded. The pages still link to the originals:</p>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: MUTED, wordBreak: 'break-all' }}>{rep.failed.filter((f) => !((f as { kind?: string }).kind === 'image' || /\.(jpe?g|png|gif|webp|avif|svg)(\?|$)/i.test(f.url))).map((f) => <li key={f.url}><a href={f.url} target="_blank" rel="noreferrer" style={{ color: INK }}>{f.url}</a> ({f.why})</li>)}</ul>
            </div>
          )}
          {!!rep.videosMoved && <p style={small}>{rep.videosMoved} {rep.videosMoved === 1 ? 'video was' : 'videos were'} moved and will play through a streaming player on the page once they finish processing, a minute or two.</p>}
          {rep.videos.length > (rep.videosMoved || 0) && <p style={small}>{rep.videos.length - (rep.videosMoved || 0)} video {rep.videos.length - (rep.videosMoved || 0) === 1 ? 'file is' : 'files are'} linked to the original address, not copied.</p>}
          {rep.builderImages > 0 && <p style={small}>{rep.builderImages} of the images came from your site builder’s image service. Keep the ones you own; stock pictures picked from the builder’s library usually can’t leave with you.</p>}
          {rep.notes.map((n) => <p key={n} style={small}>{n}</p>)}
          {/* How it should look: the moved site is a draft, so it goes straight into the
              Designer, before anything is published (Joe, Oct 29 2026: "I am not seeing how
              to access the new editor without publishing"). Matching the old site's look
              stays one click away, since only the restyle reads the old address. */}
          {!job.held && (
            <div className="flex flex-col" style={{ gap: 10, background: 'var(--hj-beige)', color: 'var(--hj-beige-ink)', borderRadius: 12, padding: 16 }}>
              <p style={{ ...body, fontWeight: 700, color: 'var(--hj-beige-ink)' }}>How should it look?</p>
              <p style={{ ...small, color: 'var(--hj-beige-ink-2)' }}>Your words and pictures are in, on plain pages. Open the Designer and say how you want it to look and read, in plain words. Your preview sits beside the chat, and nothing goes live until you publish.</p>
              <button type="button" onClick={onDesign} style={{ ...primary, alignSelf: 'flex-start' }}>Design it now</button>
              {!matchOld && <button type="button" onClick={() => setMatchOld(true)} style={{ ...linkBtn, fontSize: 14, alignSelf: 'flex-start' }}>Or match my old site’s look first</button>}
            </div>
          )}
          {matchOld && <SiteRestyle site={site} onSite={onSite} oldUrl={job.url} />}
          <p style={small}>
            Prefer your own designer or AI agent?{' '}
            <button type="button" style={{ ...linkBtn, fontSize: 14 }} onClick={() => { siteDownloadUrl(site.id, job.versionId || undefined).then((r) => { window.location.href = r.url; }).catch(() => {}); }}>Download your site’s files</button>
            {' '}and upload what comes back.
          </p>
        </div>
      )}
      {err && <p style={errText}>{err}</p>}
    </section>
  );
};

const UpdateView: React.FC<{ site: Site; onSite: (s: Site) => void; onDone: () => void; moveUrl?: string | null }> = ({ site, onSite, onDone, moveUrl }) => {
  const [rights, setRights] = useState(false);
  const [pct, setPct] = useState(0);
  const [stage, setStage] = useState<'idle' | 'uploading' | 'checking'>('idle');
  const [err, setErr] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  // The newest upload that isn't already live is the one this flow is about.
  const latest = site.versions[0] && site.versions[0].id !== site.liveVersionId ? site.versions[0] : null;
  const ready = latest && latest.status === 'ready' && site.previewVersionId === latest.id ? latest : null;
  const step = stage === 'uploading' ? 1 : stage === 'checking' ? 2 : ready ? 3 : latest ? 2 : 1;
  const go = async (file: File) => {
    setErr(null); setStage('uploading'); setPct(0);
    try { const r = await uploadSiteZip(site.id, file, (p) => { setPct(p); if (p >= 100) setStage('checking'); }); onSite(r.site); } catch (e) { setErr((e as Error).message); }
    setStage('idle');
    if (input.current) input.current.value = '';
  };
  const target = site.url.replace('https://', '');
  return (
    <div className="mx-auto flex flex-col" style={{ maxWidth: 860, gap: 24 }}>
      <div className="flex flex-col" style={{ gap: 8 }}>
        <h1 className="font-hj-display m-0" style={{ fontWeight: 800, fontSize: 'clamp(30px, 5vw, 40px)', letterSpacing: '-0.03em' }}>{site.liveVersionId ? 'Update my site' : 'Upload my site'}</h1>
        <p style={body}>Your live site doesn’t change until you publish.</p>
      </div>

      <ol className="grid" style={{ listStyle: 'none', margin: 0, padding: 0, gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
        {['Upload', 'Check', 'Preview', 'Publish'].map((s, i) => (
          <li key={s} className="flex flex-col" style={{ gap: 8 }} aria-current={i + 1 === step ? 'step' : undefined}>
            <span style={{ height: 4, borderRadius: 2, background: i + 1 < step ? INK : i + 1 === step ? 'var(--hj-yellow)' : 'var(--hj-surface-3)' }} />
            <span style={{ fontSize: 14, fontWeight: i + 1 <= step ? 600 : 400, color: i + 1 <= step ? INK : MUTED }}>{i + 1} · {s}</span>
          </li>
        ))}
      </ol>

      <section className="flex flex-col" style={{ ...panel, gap: 14 }}>
        {latest && stage === 'idle' ? (
          <div className="flex flex-wrap items-center" style={{ gap: 16 }}>
            <FileArchive size={28} strokeWidth={1.8} aria-hidden="true" />
            <div style={{ flex: '1 1 200px', minWidth: 0 }}>
              <div style={{ fontWeight: 600, wordBreak: 'break-all' }}>{latest.originalName || 'Your upload'}</div>
              <div style={small}>{latest.fileCount} files, {size(latest.bytes)} · {when(latest.uploadedAt)}</div>
            </div>
          </div>
        ) : (
          <p style={body}>A .zip of your site with index.html at the top. Up to 250 MB of pages and photos, plus up to 1.5 GB of video files; videos play through a streaming player, with 1 GB included in hosting. JavaScript is fine; server code like PHP isn’t.</p>
        )}
        <label className="flex items-start" style={{ gap: 10, fontSize: 15, cursor: 'pointer' }}>
          <Checkbox checked={rights} onCheckedChange={(v) => setRights(v === true)} style={{ marginTop: 3 }} />
          <span>I have the rights to everything I’m posting.</span>
        </label>
        <input ref={input} type="file" accept=".zip,application/zip" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) go(f); }} />
        <button type="button" disabled={!rights || stage !== 'idle'} onClick={() => input.current?.click()} style={disabled({ ...(latest ? secondary : primary), alignSelf: 'flex-start' }, !rights || stage !== 'idle')}>
          {stage === 'uploading' ? `Uploading ${pct}%` : stage === 'checking' ? <><Loader2 className="w-4 h-4 animate-spin" /> Checking your files</> : <><Upload size={16} aria-hidden="true" /> {latest ? 'Use a different file' : 'Choose zip'}</>}
        </button>
        {err && <p style={errText}>{err}</p>}
      </section>

      {stage === 'idle' && <MoveMySite site={site} onSite={onSite} initialUrl={moveUrl || undefined} onDesign={onDone} />}

      {latest && stage === 'idle' && (
        <section className="flex flex-col" style={{ ...panel, gap: 12 }}>
          <h2 style={h2}>{latest.status === 'review' ? 'Getting a quick review' : latest.status === 'rejected' ? 'We can’t publish this one' : 'We checked it'}</h2>
          {latest.status === 'ready' && <CheckLine ok>Safe to publish.</CheckLine>}
          {!!latest.videos?.length && <CheckLine ok>{latest.videos.length} {latest.videos.length === 1 ? 'video is' : 'videos are'} being prepared for streaming and will play where your video tags were. That takes a minute or two.</CheckLine>}
          {latest.status === 'review' && <p style={body}>Something in this upload needs a person to look at it, usually a password field, a redirect or scrambled code. We’ll email you when it’s ready to preview.</p>}
          {latest.problems.map((p) => <CheckLine key={p} ok={false} bad={latest.status === 'rejected'}>{p}</CheckLine>)}
        </section>
      )}

      {ready && stage === 'idle' && (
        <>
          <section className="flex flex-col" style={{ background: 'var(--hj-beige)', color: 'var(--hj-beige-ink)', borderRadius: 16, padding: 'clamp(20px, 3vw, 28px)', gap: 14 }}>
            <h2 style={{ ...h2, color: 'var(--hj-beige-ink)' }}>Look it over</h2>
            <p style={{ ...body, color: 'var(--hj-beige-ink-2)' }}>This private preview is exactly what will go live. Only you have the link. Check it on your phone too.</p>
            <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
              {site.previewUrl && <a href={site.previewUrl} target="_blank" rel="noreferrer" style={secondary}>Open preview <ExternalLink size={16} aria-hidden="true" /></a>}
              {/* The upload is the draft: change it in the Designer before publishing. */}
              <button type="button" onClick={onDone} style={secondary}>Change it in the Designer first</button>
            </div>
          </section>
          <div className="flex flex-wrap items-center justify-between" style={{ gap: 16, borderTop: '1px solid var(--hj-hairline)', paddingTop: 24 }}>
            <span style={{ ...small, flex: '1 1 240px' }}>{site.liveVersionId ? 'The current version stays saved, so you can go back to it.' : 'You can update it any time.'}</span>
            <button type="button" disabled={publishing} style={disabled({ ...primary, minHeight: 52, fontSize: 16, padding: '14px 28px', maxWidth: '100%', wordBreak: 'break-all' }, publishing)} onClick={async () => {
              setPublishing(true); setErr(null);
              try { onSite((await publishSite(site.id, ready.id)).site); onDone(); } catch (e) { setErr((e as Error).message); }
              setPublishing(false);
            }}>{publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : `Publish to ${target}`}</button>
          </div>
        </>
      )}
    </div>
  );
};

const CheckLine: React.FC<{ ok: boolean; bad?: boolean; children: React.ReactNode }> = ({ ok, bad, children }) => (
  <div className="flex items-start" style={{ gap: 10, fontSize: 15, lineHeight: 1.45, color: bad ? 'var(--hj-pink-ink)' : INK }}>
    {ok ? <Check size={18} strokeWidth={2.5} color={LIVE_FG} aria-hidden="true" style={{ flex: 'none', marginTop: 2 }} />
      : <AlertCircle size={18} aria-hidden="true" style={{ flex: 'none', marginTop: 2, color: bad ? 'var(--hj-pink-ink)' : 'var(--hj-yellow-ink)' }} />}
    <span>{children}</span>
  </div>
);

export default MySitePage;
